import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StarRating } from "@/components/StarRating";
import { UserAvatar } from "@/components/UserAvatar";
import { toast } from "sonner";
import { useReviews } from "@/hooks/useReviews";
import { CheckCircle } from "lucide-react";

interface ServiceCompletionReviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceId: string;
  providerId: string;
  providerName: string;
  providerAvatarUrl?: string;
  serviceTitle: string;
  onReviewSubmitted?: () => void;
}

export function ServiceCompletionReview({
  open,
  onOpenChange,
  serviceId,
  providerId,
  providerName,
  providerAvatarUrl,
  serviceTitle,
  onReviewSubmitted,
}: ServiceCompletionReviewProps) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { submitReview } = useReviews();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (comment.length < 10) {
      toast.error("Please write at least 10 characters for your review");
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await submitReview(serviceId, { rating, comment });
      if (success) {
        setSubmitted(true);
        onReviewSubmitted?.();
        setTimeout(() => {
          onOpenChange(false);
          setSubmitted(false);
          setRating(0);
          setComment("");
        }, 2000);
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    onOpenChange(false);
    setRating(0);
    setComment("");
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[400px]">
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold">Thank you!</h3>
            <p className="text-muted-foreground text-center">
              Your review has been submitted successfully.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-center">Service Completed!</DialogTitle>
          <DialogDescription className="text-center">
            How was your experience with {providerName}?
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-4 py-2 justify-center">
          <UserAvatar
            userId={providerId}
            avatarUrl={providerAvatarUrl}
            fallback={providerName.charAt(0)}
            size="lg"
          />
          <div>
            <h3 className="font-medium">{providerName}</h3>
            <p className="text-sm text-muted-foreground">{serviceTitle}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Rating</label>
            <div className="flex justify-center py-2">
              <StarRating value={rating} onChange={setRating} size="lg" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Your Review</label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share the details of your experience..."
              className="min-h-[120px]"
            />
            <p className="text-xs text-muted-foreground">
              Your review will help others make informed decisions.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={isSubmitting}
          >
            Skip for now
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0}
            className="bg-primary hover:bg-primary/90"
          >
            {isSubmitting ? "Submitting..." : "Submit Review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
